/**
 * Defensive normalisation only — slugs are generated server-side by the
 * `set_funding_appeal_landing_page_slug` Postgres trigger. This helper is used
 * when building public URLs to ensure a safe URL segment regardless of input.
 */
export function normalizeFundingLandingPageSlug(raw: string): string {
  const trimmed = raw.trim().toLowerCase();
  const collapsed = trimmed.replace(/[^a-z0-9-]+/g, "-").replace(/-{2,}/g, "-");
  return collapsed.replace(/^-+/, "").replace(/-+$/, "");
}

export type FundingLandingDirectoryModerationStatus =
  | "draft"
  | "pending_ai"
  | "approved_directory"
  | "rejected_directory";

export function fundingLandingPageDeniesPublicDirectoryView(page: {
  is_deleted?: boolean | null;
  directory_moderation_status?: string | null;
}): boolean {
  if (page.is_deleted) return true;
  return (page.directory_moderation_status ?? "") !== "approved_directory";
}

export function fundingLandingPageCanSubmitForDirectory(
  page: {
    directory_moderation_status?: string | null;
  },
  options?: { hasUnsavedEdits?: boolean },
): boolean {
  const s = page.directory_moderation_status ?? "draft";
  if (s === "draft" || s === "rejected_directory" || s === "approved_directory") return true;
  // Re-submit for directory review while still pending (e.g. edited draft after first submit).
  if (s === "pending_ai" && options?.hasUnsavedEdits === true) return true;
  return false;
}

/** Approved mirror row eligible for a public static URL `/{countryCode}/appeals/{slug}`. */
export function publicFundingAppealMirrorRowBuildsStaticPage(row: {
  visibility: string;
  is_deleted: boolean;
}): boolean {
  return !row.is_deleted && row.visibility === "public";
}

/** Approved mirror row eligible for an unlisted static URL including `access_token`. */
export function unlistedFundingAppealMirrorRowBuildsStaticPage(row: {
  visibility: string;
  is_deleted: boolean;
}): boolean {
  return !row.is_deleted && row.visibility === "unlisted";
}

/** Approved public mirror row eligible for `/{country}/appeals/{slug}/finished`. */
export function publicFundingAppealMirrorRowBuildsFinishedStaticPage(row: {
  visibility: string;
  is_deleted: boolean;
  success_enabled?: boolean;
}): boolean {
  return publicFundingAppealMirrorRowBuildsStaticPage(row) && row.success_enabled !== false;
}

/** Approved unlisted mirror row eligible for `/{country}/appeals/{slug}/{token}/finished`. */
export function unlistedFundingAppealMirrorRowBuildsFinishedStaticPage(row: {
  visibility: string;
  is_deleted: boolean;
  success_enabled?: boolean;
}): boolean {
  return unlistedFundingAppealMirrorRowBuildsStaticPage(row) && row.success_enabled !== false;
}

function joinUrl(base: string, path: string): string {
  const b = base.replace(/\/+$/, "");
  const p = path.replace(/^\/+/, "");
  return `${b}/${p}`;
}

/**
 * Directory appeal URLs use `/{country}/appeals/...` where `country` is the short segment
 * (e.g. `nz`). Clubs store a display label such as "New Zealand"; map that here so links
 * match `packages/club-directory` static routes.
 */
export function normalizeFundingAppealDirectoryCountrySegment(raw: string | null | undefined): string {
  const c = (raw ?? "").trim().toLowerCase() || "gb";
  if (c === "new zealand" || c === "nz") return "nz";
  return c;
}

/** Planned v1 directory paths (static build); not live until moderation + rebuild ship. */
export function fundingLandingPageDirectoryUrls(options: {
  directoryBaseUrl: string;
  countryCode: string;
  slug: string;
  visibility: "unlisted" | "public";
  accessToken: string;
}): {
  publicUrl: string;
  unlistedUrl: string;
  finishedPublicUrl: string;
  finishedUnlistedUrl: string;
} {
  const country = normalizeFundingAppealDirectoryCountrySegment(options.countryCode);
  const slug = normalizeFundingLandingPageSlug(options.slug) || options.slug.trim().toLowerCase();
  const publicPath = `${country}/appeals/${encodeURIComponent(slug)}`;
  const publicUrl = joinUrl(options.directoryBaseUrl, publicPath);
  const token = options.accessToken.trim();
  const unlistedPath = `${country}/appeals/${encodeURIComponent(slug)}/${encodeURIComponent(token)}`;
  const unlistedUrl = joinUrl(options.directoryBaseUrl, unlistedPath);
  const finishedPublicUrl = joinUrl(options.directoryBaseUrl, `${publicPath}/finished`);
  const finishedUnlistedUrl = joinUrl(options.directoryBaseUrl, `${unlistedPath}/finished`);
  return { publicUrl, unlistedUrl, finishedPublicUrl, finishedUnlistedUrl };
}

export type FundingAppealPublishedPageKind = "landing" | "finished";

/** Directory URL for a published landing or finished page, or null when not openable yet. */
export function fundingAppealPublishedPageHref(options: {
  pageKind: FundingAppealPublishedPageKind;
  directoryUrls: ReturnType<typeof fundingLandingPageDirectoryUrls>;
  visibility: "unlisted" | "public";
  /** When false, the finished page link is omitted (landing ignores this). */
  successEnabled?: boolean | null;
}): string | null {
  if (options.pageKind === "finished" && options.successEnabled === false) {
    return null;
  }
  const isPublic = options.visibility === "public";
  if (options.pageKind === "landing") {
    return isPublic ? options.directoryUrls.publicUrl : options.directoryUrls.unlistedUrl;
  }
  return isPublic ? options.directoryUrls.finishedPublicUrl : options.directoryUrls.finishedUnlistedUrl;
}

function fundingAppealLandingPageHasLiveDirectoryUrl(page: {
  directory_moderation_status?: string | null;
  is_deleted?: boolean | null;
  published_at?: string | Date | null;
}): boolean {
  if (page.is_deleted) return false;
  const status = page.directory_moderation_status ?? "draft";
  if (status === "approved_directory") return true;
  // Mirror row is kept during re-review; preview can use synced landing-page fields offline.
  if (status === "pending_ai" && page.published_at != null) return true;
  return false;
}

/** Landing-page directory URL for email preview / merge tokens (ORM-synced; works offline in Tauri). */
export function fundingAppealLandingPageDirectoryUrl(options: {
  directoryBaseUrl: string;
  countryCode: string | null | undefined;
  landingPage: {
    slug?: string | null;
    access_token?: string | null;
    visibility?: string | null;
    directory_moderation_status?: string | null;
    is_deleted?: boolean | null;
    published_at?: string | Date | null;
  };
}): string | null {
  if (!fundingAppealLandingPageHasLiveDirectoryUrl(options.landingPage)) return null;
  const slug = options.landingPage.slug?.trim();
  const accessToken = options.landingPage.access_token?.trim();
  if (!slug || !accessToken) return null;
  const visibility = options.landingPage.visibility === "public" ? "public" : "unlisted";
  const directoryUrls = fundingLandingPageDirectoryUrls({
    directoryBaseUrl: options.directoryBaseUrl,
    countryCode: options.countryCode ?? "gb",
    slug,
    visibility,
    accessToken,
  });
  return fundingAppealPublishedPageHref({
    pageKind: "landing",
    directoryUrls,
    visibility,
  });
}
